import React, { useRef, useState } from "react";
import { Target, PhoneCall, CalendarCheck, Palette, Code, Globe, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const services = [
  {
    title: "Lead Generation",
    icon: <Target size={22} strokeWidth={1.8} />,
    text: "Identify and attract potential clients through targeted outreach, inbound marketing, and prospecting strategies. Build a predictable pipeline that fuels long-term sales growth.",
    labels: ["B2B Leads", "Prospecting", "Outbound Outreach", "Sales Pipeline"],
    img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80",
    accent: "#9333ea",
  },
  {
    title: "Appointment Setting & Sales Closing",
    icon: <CalendarCheck size={22} strokeWidth={1.8} />,
    text: "We book qualified appointments with decision-makers and actively close deals through structured follow-ups, persuasive communication, and proven sales frameworks.",
    labels: ["Appointment Setting", "Deal Conversion", "Lead Qualification", "High-Ticket Sales"],
    img: "https://images.unsplash.com/photo-1556761175-4b46a572b786?w=600&q=80",
    accent: "#7b19bc",
  },
  {
    title: "CRM Management",
    icon: <PhoneCall size={22} strokeWidth={1.8} />,
    text: "We manage CRM systems to track every interaction, automate follow-ups, and execute calling campaigns that move prospects through the sales funnel efficiently.",
    labels: ["CRM Management", "Sales Automation", "Pipeline Tracking", "Client Engagement"],
    img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80",
    accent: "#6d28d9",
  },
  {
    title: "Web Development",
    icon: <Code size={22} strokeWidth={1.8} />,
    text: "Build fast, scalable web applications using MongoDB, Express, React, and Node.js. We deliver conversion-focused full-stack solutions tailored to modern business needs.",
    labels: ["MERN Stack", "Full Stack", "ReactJS", "NodeJS"],
    img: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=600&q=80",
    accent: "#7c3aed",
  },
  {
    title: "Design & Creative",
    icon: <Palette size={22} strokeWidth={1.8} />,
    text: "Create visually compelling designs, branding systems, and digital assets that strengthen brand identity and increase engagement across all platforms.",
    labels: ["Branding", "UI/UX", "Creative Strategy", "Visual Identity"],
    img: "https://images.unsplash.com/photo-1561070791-2526d30994b5?w=600&q=80",
    accent: "#a21caf",
  },
  {
    title: "Digital Marketing",
    icon: <Globe size={22} strokeWidth={1.8} />,
    text: "Launch performance-driven digital campaigns across SEO, PPC, social media, and email marketing to attract, engage, and convert high-intent audiences.",
    labels: ["SEO", "Social Media", "PPC Advertising", "Brand Growth"],
    img: "https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=600&q=80",
    accent: "#7b19bc",
  },
];

const steps = [
  { n: "01", title: "Understand Your Goals", text: "We begin by understanding your sales targets, market position, and growth objectives to align every strategy with measurable revenue outcomes." },
  { n: "02", title: "Build a Custom Strategy", text: "Our team creates a tailored strategy combining lead generation, appointment setting, sales closing, and marketing — all mapped to your funnel." },
  { n: "03", title: "Design, Develop & Create", text: "We design engaging visuals, develop high-performing websites, and create assets that support conversions at every stage of the customer journey." },
  { n: "04", title: "Launch & Execute", text: "Campaigns go live across outbound, inbound, and digital channels. We actively manage outreach, appointments, and sales conversations." },
  { n: "05", title: "Optimize & Scale", text: "We analyze performance, optimize what converts best, and scale successful strategies to drive consistent and sustainable growth." },
];

export default function Ourservices() {
  const scrollRef = useRef(null);
  const [active, setActive] = useState(0);

  const scroll = (dir) => {
    const el = scrollRef.current;
    const w = el.firstChild.offsetWidth + 28;
    el.scrollBy({ left: dir === "left" ? -w : w, behavior: "smooth" });
    setActive((p) => dir === "left" ? Math.max(0, p - 1) : Math.min(services.length - 1, p + 1));
  };

  return (
    <div style={{ fontFamily: '"Elza", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      {/* ══════════════════════════════════════════
          SERVICES SECTION
      ══════════════════════════════════════════ */}
      <section style={{ background: "#05050a", color: "#fff", padding: "120px 0 100px", overflow: "hidden", position: "relative" }}>

        {/* Background grid */}
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(123,25,188,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(123,25,188,0.06) 1px, transparent 1px)", backgroundSize: "60px 60px", pointerEvents: "none" }} />

        {/* Glow orb */}
        <div style={{ position: "absolute", top: "-200px", left: "50%", transform: "translateX(-50%)", width: "800px", height: "600px", background: "radial-gradient(ellipse, rgba(123,25,188,0.18) 0%, transparent 65%)", pointerEvents: "none" }} />

        <div style={{ position: "relative", zIndex: 1, maxWidth: "1300px", margin: "0 auto", padding: "0 40px" }}>

          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "64px", flexWrap: "wrap", gap: "20px" }}>
            <div>
              <span style={{ display: "inline-block", fontSize: "11px", fontWeight: 700, letterSpacing: "3px", textTransform: "uppercase", color: "#b06aff", marginBottom: "14px" }}>What We Do</span>
              <h2 style={{ fontSize: "clamp(28px, 4vw, 52px)", fontWeight: 800, lineHeight: 1.15, margin: 0, maxWidth: "620px", letterSpacing: "-0.5px" }}>
                Every service built to{" "}
                <span style={{ background: "linear-gradient(90deg,#b06aff,#e040fb)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>generate leads</span>,<br />close deals &amp; scale revenue
              </h2>
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <button onClick={() => scroll("left")} style={{ width: "48px", height: "48px", borderRadius: "50%", border: "1px solid rgba(176,106,255,0.3)", background: "rgba(123,25,188,0.1)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }} onMouseEnter={e => e.currentTarget.style.background = "rgba(123,25,188,0.4)"} onMouseLeave={e => e.currentTarget.style.background = "rgba(123,25,188,0.1)"}>
                <ChevronLeft size={20} />
              </button>
              <button onClick={() => scroll("right")} style={{ width: "48px", height: "48px", borderRadius: "50%", border: "1px solid rgba(176,106,255,0.3)", background: "rgba(123,25,188,0.1)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }} onMouseEnter={e => e.currentTarget.style.background = "rgba(123,25,188,0.4)"} onMouseLeave={e => e.currentTarget.style.background = "rgba(123,25,188,0.1)"}>
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          {/* Cards track */}
          <div ref={scrollRef} style={{ display: "flex", gap: "28px", overflowX: "auto", scrollSnapType: "x mandatory", scrollbarWidth: "none", paddingBottom: "8px" }}>
            {services.map((s, i) => (
              <div key={i} style={{ flex: "0 0 calc(33.33% - 20px)", minWidth: "320px", scrollSnapAlign: "start", borderRadius: "24px", overflow: "hidden", border: "1px solid rgba(176,106,255,0.15)", background: "rgba(255,255,255,0.03)", backdropFilter: "blur(10px)", transition: "transform 0.35s, box-shadow 0.35s" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-8px)"; e.currentTarget.style.boxShadow = `0 30px 60px rgba(123,25,188,0.3)`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}>

                {/* Card image */}
                <div style={{ position: "relative", height: "200px", overflow: "hidden" }}>
                  <img src={s.img} alt={s.title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to bottom, transparent 30%, rgba(5,5,10,0.95) 100%)` }} />
                  <div style={{ position: "absolute", top: "16px", right: "16px", width: "44px", height: "44px", borderRadius: "12px", background: "rgba(123,25,188,0.85)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
                    {s.icon}
                  </div>
                </div>

                {/* Card body */}
                <div style={{ padding: "28px" }}>
                  <h3 style={{ fontSize: "19px", fontWeight: 700, margin: "0 0 12px", color: "#fff", lineHeight: 1.3 }}>{s.title}</h3>
                  <p style={{ fontSize: "14px", color: "#8a82a0", lineHeight: 1.75, margin: "0 0 22px" }}>{s.text}</p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {s.labels.map((l) => (
                      <span key={l} style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.5px", padding: "5px 12px", borderRadius: "50px", border: "1px solid rgba(176,106,255,0.3)", color: "#c4a8f0", background: "rgba(123,25,188,0.08)" }}>{l}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dots */}
          <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "36px" }}>
            {services.map((_, i) => (
              <div key={i} style={{ width: i === active ? "28px" : "8px", height: "8px", borderRadius: "4px", background: i === active ? "#b06aff" : "rgba(176,106,255,0.25)", transition: "all 0.3s" }} />
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          WORKFLOW SECTION
      ══════════════════════════════════════════ */}
      <section style={{ background: "#fff", color: "#111", padding: "120px 40px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>

          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: "80px" }}>
            <span style={{ display: "inline-block", fontSize: "11px", fontWeight: 700, letterSpacing: "3px", textTransform: "uppercase", color: "#7b19bc", marginBottom: "14px" }}>How We Work</span>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 50px)", fontWeight: 800, margin: "0 auto", maxWidth: "700px", lineHeight: 1.15, letterSpacing: "-0.5px", color: "#0a0a0a" }}>
              Our Sales &amp; Marketing Workflow —{" "}
              <span style={{ background: "linear-gradient(90deg,#7b19bc,#b06aff)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Built to Convert</span>
            </h2>
          </div>

          {/* Steps — alternating layout */}
          <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
            {steps.map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "stretch", gap: "0", borderBottom: i < steps.length - 1 ? "1px solid #f0eaf8" : "none" }}>

                {/* Number column */}
                <div style={{ width: "120px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "48px 0" }}>
                  <span style={{ fontSize: "13px", fontWeight: 800, letterSpacing: "2px", color: "#d4b8f5", fontVariantNumeric: "tabular-nums" }}>{s.n}</span>
                </div>

                {/* Vertical line */}
                <div style={{ width: "1px", background: "linear-gradient(to bottom, transparent, rgba(123,25,188,0.2), transparent)", flexShrink: 0 }} />

                {/* Content */}
                <div style={{ flex: 1, padding: "48px 48px 48px 48px", display: "flex", alignItems: "center", gap: "40px", flexWrap: "wrap" }}>
                  <div style={{ flex: "0 0 auto" }}>
                    <div style={{ width: "52px", height: "52px", borderRadius: "16px", background: "linear-gradient(135deg, #7b19bc, #b06aff)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "22px", fontWeight: 800 }}>
                      {i + 1}
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: "200px" }}>
                    <h3 style={{ fontSize: "20px", fontWeight: 700, margin: "0 0 10px", color: "#0a0a0a" }}>{s.title}</h3>
                    <p style={{ fontSize: "15px", color: "#666", lineHeight: 1.75, margin: 0 }}>{s.text}</p>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <div style={{ width: "40px", height: "40px", borderRadius: "50%", border: "1px solid rgba(123,25,188,0.2)", display: "flex", alignItems: "center", justifyContent: "center", color: "#b06aff" }}>
                      <ArrowRight size={18} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA strip */}
          <div style={{ marginTop: "72px", borderRadius: "24px", background: "linear-gradient(135deg, #0d0d0d 0%, #1a0a2e 60%, #2a0845 100%)", padding: "56px 60px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "28px", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: "-80px", right: "-80px", width: "400px", height: "400px", background: "radial-gradient(circle, rgba(176,106,255,0.2) 0%, transparent 65%)", pointerEvents: "none" }} />
            <div style={{ position: "relative", zIndex: 1 }}>
              <h3 style={{ fontSize: "clamp(22px, 3vw, 34px)", fontWeight: 800, color: "#fff", margin: "0 0 10px", letterSpacing: "-0.3px" }}>Ready to grow your business?</h3>
              <p style={{ fontSize: "16px", color: "#9b8fb5", margin: 0 }}>Let's build a strategy tailored to your goals.</p>
            </div>
            <a href="#contact" style={{ position: "relative", zIndex: 1, display: "inline-flex", alignItems: "center", gap: "10px", padding: "16px 32px", background: "linear-gradient(135deg, #7b19bc, #b06aff)", color: "#fff", fontWeight: 700, fontSize: "15px", textDecoration: "none", borderRadius: "12px", letterSpacing: "0.3px", transition: "opacity 0.2s, transform 0.2s" }} onMouseEnter={e => { e.currentTarget.style.opacity = "0.9"; e.currentTarget.style.transform = "translateY(-2px)"; }} onMouseLeave={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "none"; }}>
              Get Started <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
